import { useMemo, useState, useCallback, useEffect } from 'react';
import { useDynamicEntity } from './useDynamicEntity';
import { useAuth } from './index';
import { useAccessibleFirmIds } from './useAccessibleFirmIds';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_CONTRACTS_FOR_REFERENCE_QUERY } from '../graphql/queries';
import { formatDateForHasura } from '../utils/dateUtils';

// Yenileme durumu hesabı:
//   1) contract_renewals tablosunda kayıt varsa → MAX(renewal_end_date)
//      next_renewal_date'tir. (Backend evaluator ile aynı mantık.)
//   2) Hiç yenileme yapılmamışsa → signing_date + renewal_period ay.
//
// Çıkan tarihle bugün arasındaki fark:
//   < 0   gün → 'overdue'  (kırmızı, priority 2)
//   0-30  gün → 'due_soon' (turuncu, priority 1)
//   > 30  gün → 'upcoming' (renksiz, priority 0)
//
// auto_renewal=false veya signing_date / renewal_period eksikse 'no_renewal'.
const computeRenewalStatus = (contract) => {
  const NO_RENEWAL = {
    renewal_status: 'no_renewal',
    renewal_priority: 0,
    days_to_renewal: null,
    next_renewal_date: null,
    last_renewal_end_date: null,
  };
  if (!contract.auto_renewal) return NO_RENEWAL;

  const renewals = Array.isArray(contract.renewals) ? contract.renewals : [];
  let nextRenewal = null;
  let lastEnd = null;

  if (renewals.length > 0) {
    // renewals already ordered desc by query, ama defansif olarak sort.
    const sorted = [...renewals].sort((a, b) => {
      const ta = a.renewal_end_date ? new Date(a.renewal_end_date).getTime() : 0;
      const tb = b.renewal_end_date ? new Date(b.renewal_end_date).getTime() : 0;
      return tb - ta;
    });
    if (sorted[0]?.renewal_end_date) {
      lastEnd = sorted[0].renewal_end_date;
      nextRenewal = new Date(sorted[0].renewal_end_date);
    }
  }

  if (!nextRenewal) {
    if (!contract.contract_signing_date) return NO_RENEWAL;
    const period = parseInt(contract.renewal_period);
    if (!Number.isFinite(period) || period <= 0) return NO_RENEWAL;
    const signing = new Date(contract.contract_signing_date);
    if (isNaN(signing.getTime())) return NO_RENEWAL;
    nextRenewal = new Date(signing);
    nextRenewal.setMonth(nextRenewal.getMonth() + period);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.floor((nextRenewal - today) / (1000 * 60 * 60 * 24));
  const nextIso = nextRenewal.toISOString().slice(0, 10);
  const common = {
    days_to_renewal: days,
    next_renewal_date: nextIso,
    last_renewal_end_date: lastEnd,
  };
  if (days < 0) return { renewal_status: 'overdue', renewal_priority: 2, ...common };
  if (days <= 30) return { renewal_status: 'due_soon', renewal_priority: 1, ...common };
  return { renewal_status: 'upcoming', renewal_priority: 0, ...common };
};

const useContracts = ({ paginated = false } = {}) => {
  const { user } = useAuth();
  const { firmIds, loading: firmIdsLoading, isRestricted } = useAccessibleFirmIds();
  const [referenceContracts, setReferenceContracts] = useState([]);

  const fetchReferenceContracts = useCallback(async () => {
    if (!user?.companyId) return;
    try {
      const data = await hasuraClient.query(GET_CONTRACTS_FOR_REFERENCE_QUERY, {
        companyId: user.companyId,
      });
      setReferenceContracts(data.contracts || []);
    } catch (error) {
      console.error('Error fetching reference contracts:', error);
    }
  }, [user?.companyId]);

  useEffect(() => {
    fetchReferenceContracts();
  }, [fetchReferenceContracts]);

  const customWhereCondition = useMemo(() => {
    if (user?.role === 'ROOT') return {};
    if (isRestricted) {
      const safeIds = firmIds && firmIds.length > 0 ? firmIds : [-1];
      return { company_id: { _eq: user?.companyId }, firm_id: { _in: safeIds } };
    }
    return { company_id: { _eq: user?.companyId } };
  }, [user?.role, user?.companyId, isRestricted, firmIds]);

  const firmIdsKey = isRestricted ? (firmIds ? firmIds.join(',') : 'loading') : 'unrestricted';
  const dynamicHook = useDynamicEntity({
    entityType: 'contracts',
    gqlEntityKey: 'contracts',
    queryName: 'GET_CONTRACTS_QUERY',
    createMutation: 'CREATE_CONTRACT_MUTATION',
    updateMutation: 'UPDATE_CONTRACT_MUTATION',
    deleteMutation: 'DELETE_CONTRACT_MUTATION',
    updateVariableName: 'set',
    serverPagination: paginated,
    aggregateKey: paginated ? 'contracts_aggregate' : null,
    transformData: (contract) => ({
      ...contract,
      ...computeRenewalStatus(contract),
      company_name: contract.company?.name || `Company ${contract.company_id}`,
      firm_name: contract.firm?.firm_name || contract.firm_short_name || `Firm ${contract.firm_id}`,
      firm_short_name: contract.firm_short_name || contract.firm?.firm_short_name || '',
      contract_subject_name: contract.contract_subject?.name || `Subject ${contract.contract_subject_id}`,
      scope_of_work_name: contract.scope_of_work?.name || `Scope ${contract.scope_of_work_id}`,
      pricing_method_name: contract.pricing_method?.name || `Pricing ${contract.pricing_method_id}`,
      work_status_name: contract.work_status?.name || `Status ${contract.work_status_id}`,
      auto_renewal: contract.auto_renewal || false,
      renewal_period: contract.renewal_period || null,
      inflation_clause: contract.inflation_clause || false,
      work_started_without_contract: contract.work_started_without_contract === true ? 'Geldi' : 'Gelmedi',
      project_numbers: (contract.jobs || [])
        .map(j => j?.project_number)
        .filter(Boolean)
        .join(', '),
      companyId: contract.company_id,
      firmId: contract.firm_id,
    }),
    transformFormData: (formData) => {
      // contracts.* date columns are `timestamp` (no TZ). Send a stable
      // "YYYY-MM-DDT00:00:00" derived from the picker's LOCAL Y/M/D so that
      // the saved date never shifts due to UTC conversion (Bug 1).
      const normalizeDate = (value) => {
        const localYmd = formatDateForHasura(value);
        return localYmd ? `${localYmd}T00:00:00` : null;
      };

      let firm_id = null;
      let firm_short_name = null;
      
      if (formData.firm_established === 'yes') {
        firm_id = formData.firmId;
        firm_short_name = null;
      } else {
        // firm_short_name seçimi aslında firm_id'yi temsil ediyor
        firm_id = formData.firm_short_name; // Bu artık firm_id
        firm_short_name = null;
      }
      
      const result = {
        contract_subject_id: formData.contract_subject_id,
        scope_of_work_id: formData.scope_of_work_id,
        pricing_method_id: formData.pricing_method_id,
        work_status_id: formData.work_status_id,
        contract_signing_date: normalizeDate(formData.contract_signing_date),
        invoice_issue_date: normalizeDate(formData.invoice_issue_date),
        auto_renewal: formData.auto_renewal === 'yes',
        renewal_period: formData.renewal_period,
        first_price_increase_period: normalizeDate(formData.first_price_increase_period),
        inflation_clause: formData.inflation_clause === 'yes',
        work_start_date: normalizeDate(formData.work_start_date),
        work_started_without_contract: formData.work_started_without_contract === 'yes',
        protocol_start_date: normalizeDate(formData.protocol_start_date),
        protocol_end_date: normalizeDate(formData.protocol_end_date),
        comment: formData.comment || null,
        contract_description: formData.contract_description || null,
        company_id: formData.companyId,
        firm_id: firm_id,
        firm_short_name: firm_short_name,
        // PDF alanları
        contract_pdf_key: formData.contract_pdf === null ? null : (formData.contract_pdf?.key || formData.contract_pdf_key || null),
        protocol_pdf_key: formData.protocol_pdf === null ? null : (formData.protocol_pdf?.key || formData.protocol_pdf_key || null),
        contract_pdf_size: formData.contract_pdf === null ? null : (formData.contract_pdf?.size || formData.contract_pdf_size || null),
        protocol_pdf_size: formData.protocol_pdf === null ? null : (formData.protocol_pdf?.size || formData.protocol_pdf_size || null),
      };
      
      return result;
    },
    customWhereCondition,
    customDepsKey: firmIdsKey,
  });
  // Initial sıralama: renewal_priority DESC (overdue=2, due_soon=1, diğer=0),
  // priority eşitse contract_signing_date desc. Kullanıcı UI'da kolon başlığı
  // tıklayınca DataTable kendi sortConfig'ine geçer ve bunu override eder.
  const sortedContracts = useMemo(() => {
    const list = dynamicHook?.contracts || [];
    if (!Array.isArray(list) || list.length === 0) return list;
    return [...list].sort((a, b) => {
      const dp = (b.renewal_priority || 0) - (a.renewal_priority || 0);
      if (dp !== 0) return dp;
      const da = a.contract_signing_date ? new Date(a.contract_signing_date).getTime() : 0;
      const db = b.contract_signing_date ? new Date(b.contract_signing_date).getTime() : 0;
      return db - da;
    });
  }, [dynamicHook.contracts]);

  return {
    ...dynamicHook,
    contracts: sortedContracts,
    isLoading: dynamicHook.isLoading || (isRestricted && firmIdsLoading),
    isRootUser: user?.role === 'ROOT',
    referenceContracts,
  };
};

export { useContracts }; 